import { create } from "zustand";
import {
  createHotspot as createLocalHotspot,
  removeHotspot as removeLocalHotspot,
  updateHotspot as updateLocalHotspot,
} from "@/data/adapters/local/hotspots";
import { updatePanorama as updateLocalPanorama } from "@/data/adapters/local/panoramas";
import { convex } from "@/lib/convexClient";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { StorageService } from "@/services/storageService";
import type { Tables } from "@/types/database";
import { getPanoramaMediaType } from "@/utils/panoramaMedia";
import { api } from "../../convex/_generated/api";

/**
 * Centralised editor state shared across components.
 * Will progressively replace prop-drilled state in Editor.tsx.
 */

// Extended Panorama type for discovered panoramas
interface ExtendedPanorama extends Tables<"panoramas"> {
  isDiscovered?: boolean;
  data?: {
    storage_path?: string;
    main_file?: string;
    thumbnail_file?: string;
    thumbnail_webp_file?: string;
    [key: string]: unknown;
  };
}

export type Panorama = ExtendedPanorama;
type Project = Tables<"projects">;
export type Hotspot = Tables<"hotspots">;
type SkinComponent = Record<string, unknown>;
type StorageEntry = { name?: string; id?: string };
type UserRef = { id: string };
const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

type HotspotSnapshot = Pick<
  Hotspot,
  | "id"
  | "project_id"
  | "panorama_id"
  | "name"
  | "type"
  | "content"
  | "position"
  | "rotation"
  | "scale"
  | "visible"
  | "order"
> & {
  cms_action?: unknown;
  cmsAction?: unknown;
};

type HotspotHistoryAction =
  | { type: "create"; key: string; hotspot: HotspotSnapshot }
  | { type: "delete"; key: string; hotspot: HotspotSnapshot }
  | { type: "update"; key: string; before: HotspotSnapshot; after: HotspotSnapshot };

type PanoramaSnapshot = Pick<
  Panorama,
  | "id"
  | "project_id"
  | "name"
  | "order"
  | "width"
  | "height"
  | "file_size"
  | "file_url"
  | "private_file_url"
  | "thumbnail_url"
  | "thumbnail_url_webp"
  | "file_status"
  | "processing_status"
  | "gps_location"
  | "data"
> & {
  [key: string]: unknown;
};

type EditorHistoryAction =
  | HotspotHistoryAction
  | {
      type: "panorama_update";
      panoramaId: string;
      before: PanoramaSnapshot;
      after: PanoramaSnapshot;
    };

type SkinComponentSnapshot = SkinComponent & {
  [key: string]: unknown;
};

type SkinHistoryAction =
  | { type: "skin_create"; component: SkinComponentSnapshot; index: number }
  | { type: "skin_delete"; component: SkinComponentSnapshot; index: number }
  | {
      type: "skin_update";
      componentId: string;
      before: SkinComponentSnapshot;
      after: SkinComponentSnapshot;
    }
  | { type: "skin_reorder"; before: SkinComponentSnapshot[]; after: SkinComponentSnapshot[] };

type EditorHistoryActionWithSkin = EditorHistoryAction | SkinHistoryAction;

const buildHotspotUpdateArgs = (id: string, updates: Partial<Hotspot>) => {
  const args: Record<string, unknown> = { id };
  for (const key of [
    "name",
    "type",
    "content",
    "position",
    "rotation",
    "scale",
    "visible",
    "order",
  ] as const) {
    if (updates[key] !== undefined) {
      args[key] = updates[key];
    }
  }
  return args;
};

const buildPanoramaUpdateArgs = (id: string, updates: Partial<Panorama>) => {
  const args: Record<string, unknown> = { id };
  for (const key of [
    "name",
    "order",
    "width",
    "height",
    "file_size",
    "file_url",
    "private_file_url",
    "thumbnail_url",
    "thumbnail_url_webp",
    "file_status",
    "processing_status",
    "gps_location",
    "data",
  ] as const) {
    if (updates[key] !== undefined) {
      args[key] = updates[key];
    }
  }
  return args;
};

const shouldUseLocalBackend = (state: { projectBackend: "local" | "cloud" | null }) =>
  isElectron() && state.projectBackend === "local";

interface HotspotMovementUndo {
  hotspotId: string;
  previousPosition: { x: number; y: number; z: number };
}

interface EditorState {
  // Data
  project: Project | null;
  panoramas: Panorama[];
  hotspots: Hotspot[];
  skinComponents: SkinComponent[];

  // UI selections
  selectedPanorama: Panorama | null;
  selectedPanoramaIds: string[]; // For multi-select
  selectedHotspot: Hotspot | null;
  selectedHotspotIds: string[]; // For bulk editing
  selectedSkinComponent: SkinComponent | null;
  placementMode: boolean;
  placementType: "svg" | "html" | "video";
  htmlPlacementContentType: "text" | "icon";
  isDraggingToAddHotspot: boolean;
  pendingHotspotPlacementId: string | null;

  // Undo helpers
  deletedHotspots: Hotspot[];
  hotspotMovementUndo: HotspotMovementUndo | null;
  undoStack: EditorHistoryActionWithSkin[];
  redoStack: EditorHistoryActionWithSkin[];
  hotspotKeyById: Record<string, string>;
  hotspotIdByKey: Record<string, string | null>;
  isApplyingHistory: boolean;

  // Flags
  loading: boolean;
  localLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  isPanelSwitching: boolean;

  // UI State
  activeTab: string;
  hoveredHotspot: Hotspot | null;
  isControlsActive: boolean;
  placementPreview: { x: number; y: number; z: number } | null;
  hotspotTooltip: { x: number; y: number; name: string } | null;
  livePosition: { x: number; y: number; z: number } | null;
  showPanoramaProperties: boolean;
  chromaKeyPickHotspotId: string | null;
  hotspotPanelTab: "style" | "settings" | "actions" | null;
  hotspotTextAutoFocus: boolean;

  // Viewer Feature Flags
  enableHotspotPlacement: boolean;
  enableHotspotEditing: boolean;
  enableSkinComponentEditing: boolean;
  showPlacementButton: boolean;
  currentRoute: "editor" | "skin" | "cms" | "settings" | "trash";

  // CMS data binding state
  activeCMSDatasetId: string | null;
  activeCMSRowIndex: number;
  resolvedBindings: Record<string, Record<string, string>>;
  currentCMSRowData: unknown;
  viewerVisible: boolean;
  isPreviewMode: boolean;
  hotspotRenderStyle: "billboard" | "3d";
  isAspectRatioLocked: boolean;
  skinViewportWidth: number | null;
  skinViewportPreset: string | null;

  setChromaKeyPickHotspotId: (hotspotId: string | null) => void;
  setHotspotPanelTab: (tab: "style" | "settings" | "actions" | null) => void;
  setHotspotTextAutoFocus: (autoFocus: boolean) => void;
  setHotspotPanelTab: (tab: "style" | "settings" | "actions" | null) => void;

  // ───────────────────────────── Actions ─────────────────────────────
  setProject: (p: Project | null) => void;
  setLoading: (l: boolean) => void;
  setLocalLoading: (l: boolean) => void;
  setIsSaving: (s: boolean) => void;
  setIsDirty: (d: boolean) => void;
  setIsPanelSwitching: (s: boolean) => void;

  setPanoramas: (panos: Panorama[]) => void;
  setHotspots: (h: Hotspot[]) => void;
  setSkinComponents: (c: SkinComponent[]) => void;

  addHotspot: (h: Hotspot) => void;
  updateHotspot: (id: string, updates: Partial<Hotspot>) => void;
  deleteHotspot: (id: string) => void;
  duplicateHotspot: (hotspot: Hotspot) => Promise<void>;

  setSelectedPanorama: (p: Panorama | null) => void;
  setSelectedPanoramaIds: (ids: string[]) => void;
  setSelectedHotspot: (h: Hotspot | null) => void;
  setSelectedHotspotIds: (ids: string[]) => void;
  toggleHotspotSelection: (id: string) => void;
  clearHotspotSelection: () => void;
  selectAllHotspots: () => void;
  setSelectedSkinComponent: (c: SkinComponent | null) => void;

  setPlacementMode: (b: boolean) => void;
  setPlacementType: (placementType: "svg" | "html" | "video") => void;
  setHtmlPlacementContentType: (contentType: "text" | "icon") => void;
  setIsDraggingToAddHotspot: (b: boolean) => void;
  setPendingHotspotPlacementId: (id: string | null) => void;

  pushDeletedHotspot: (h: Hotspot) => void;
  popDeletedHotspot: () => Hotspot | undefined;

  setHotspotMovementUndo: (u: HotspotMovementUndo | null) => void;
  recordHotspotCreate: (hotspot: Hotspot) => void;
  recordHotspotDelete: (hotspot: Hotspot) => void;
  recordHotspotUpdate: (
    hotspotId: string,
    updates: Partial<Hotspot>,
    options?: { beforeOverride?: Partial<HotspotSnapshot> },
  ) => void;
  recordHotspotUpdateWithSnapshots: (
    hotspotId: string,
    before: HotspotSnapshot,
    after: HotspotSnapshot,
  ) => void;
  recordPanoramaUpdate: (
    panoramaId: string,
    updates: Partial<Panorama>,
    options?: { beforeOverride?: Partial<PanoramaSnapshot> },
  ) => void;
  recordSkinComponentCreate: (component: SkinComponent, index?: number) => void;
  recordSkinComponentDelete: (component: SkinComponent, index: number) => void;
  recordSkinComponentUpdate: (
    componentId: string,
    updates: Partial<SkinComponent>,
    options?: { beforeOverride?: Partial<SkinComponentSnapshot> },
  ) => void;
  recordSkinComponentsReorder: (before: SkinComponent[], after: SkinComponent[]) => void;
  undoHotspotEdit: () => Promise<void>;
  redoHotspotEdit: () => Promise<void>;

  // UI State Actions
  setActiveTab: (tab: string) => void;
  setHoveredHotspot: (h: Hotspot | null) => void;
  setIsLoading: (l: boolean) => void;
  setIsControlsActive: (c: boolean) => void;
  setPlacementPreview: (p: { x: number; y: number; z: number } | null) => void;
  setHotspotTooltip: (t: { x: number; y: number; name: string } | null) => void;
  setLivePosition: (p: { x: number; y: number; z: number } | null) => void;
  setShowPanoramaProperties: (show: boolean) => void;

  // Viewer Feature Flag Actions
  setEnableHotspotPlacement: (enable: boolean) => void;
  setEnableHotspotEditing: (enable: boolean) => void;
  setEnableSkinComponentEditing: (enable: boolean) => void;
  setShowPlacementButton: (show: boolean) => void;
  setCurrentRoute: (route: "editor" | "skin" | "settings" | "trash" | "cms" | "variables") => void;

  // CMS actions
  setActiveCMSRecord: (datasetId: string | null, rowIndex: number) => void;
  setResolvedBindings: (bindings: Record<string, Record<string, string>>) => void;
  setCurrentCMSRowData: (data: unknown) => void;
  setViewerVisible: (visible: boolean) => void;
  setIsPreviewMode: (isPreview: boolean) => void;
  setHotspotRenderStyle: (style: "billboard" | "3d") => void;
  setSkinViewportState: (state: { width: number | null; preset: string | null }) => void;

  // Skin Component Actions
  addSkinComponent: (type: string, additionalData?: Record<string, unknown>) => void;
  addSkinComponentWithData: (component: SkinComponent) => void;
  updateSkinComponent: (id: string, updates: Partial<Record<string, unknown>>) => void;
  deleteSkinComponent: (id: string) => void;
  reorderSkinComponents: (components: SkinComponent[]) => void;
  nestSkinComponent: (childId: string, parentId: string | null) => void;

  // Additional actions for SceneHierarchy
  toggleHotspotVisibility: (hotspotId: string) => Promise<void>;
  uploadPanoramas: (
    files: FileList,
    uploadPreference?: "original-only" | "compressed-only",
    mediaType?: "panorama" | "zoomable_image",
  ) => Promise<void>;
  deleteHotspotWithUndo: (hotspotId: string) => Promise<void>;

  // Additional actions for viewer
  updateHotspotFromViewer: (
    hotspotId: string,
    updates: Partial<Hotspot>,
    options?: { historyBeforeOverride?: Partial<HotspotSnapshot> },
  ) => Promise<void>;
  requestHotspotPlacement: (
    position: { x: number; y: number; z: number },
    user: unknown,
  ) => Promise<void>;

  // Additional actions for PropertiesPanel
  updateHotspotFromPanel: (hotspotId: string, updates: Partial<Hotspot>) => Promise<void>;
  deleteHotspotFromPanel: (hotspotId: string) => Promise<void>;

  // Update panorama in store
  updatePanorama: (panoramaId: string, updates: Partial<Panorama>) => void;

  // Additional actions for PanoramaPropertiesPanel
  updatePanoramaFromPanel: (panoramaId: string, updates: Partial<Panorama>) => Promise<void>;
  deletePanoramaFromPanel: (panoramaId: string) => Promise<void>;

  // Hotspot Visibility Actions
  showHotspots: (hotspotIds: string[]) => void;
  hideHotspots: (hotspotIds: string[]) => void;
  toggleHotspots: (hotspotIds: string[]) => void;
  setHotspotVisibility: (hotspotId: string, visible: boolean) => void;

  // Skin Visibility Actions
  showSkinComponents: (componentIds: string[]) => void;
  hideSkinComponents: (componentIds: string[]) => void;
  toggleSkinComponents: (componentIds: string[]) => void;

  // Bulk editing actions
  bulkUpdateHotspots: (hotspotIds: string[], updates: Partial<Hotspot>) => Promise<void>;
  bulkDeleteHotspots: (hotspotIds: string[]) => Promise<void>;

  refreshPanoramas: (projectId: string) => Promise<Panorama[]>;
  discoverPanoramasFromStorage: (craftspaceId: string, projectId: string) => Promise<Panorama[]>;
  syncDiscoveredPanoramas: (craftspaceId: string, projectId: string) => Promise<Panorama[]>;

  // Camera view actions
  currentCameraView: { yaw: number; pitch: number; fov: number } | null;
  setCurrentCameraView: (view: { yaw: number; pitch: number; fov: number } | null) => void;
  defaultViewSaveEvent: {
    panoramaId: string;
    view: { yaw: number; pitch: number; fov: number };
    nonce: number;
  } | null;
  setDefaultViewSaveEvent: (
    event: {
      panoramaId: string;
      view: { yaw: number; pitch: number; fov: number };
      nonce: number;
    } | null,
  ) => void;
  pendingPanoramaView: {
    panoramaId: string;
    view: { yaw: number; pitch: number; fov: number };
  } | null;
  setPendingPanoramaView: (
    payload: { panoramaId: string; view: { yaw: number; pitch: number; fov: number } } | null,
  ) => void;
  focusHotspotRequestId: string | null;
  requestFocusHotspot: (hotspotId: string | null) => void;
  projectBackend: "local" | "cloud" | null;
  setProjectBackend: (backend: "local" | "cloud" | null) => void;
}

export const useEditorStore = create<EditorState>((set, get) => {
  const buildHotspotSnapshot = (hotspot: Hotspot): HotspotSnapshot => ({
    id: hotspot.id,
    project_id: hotspot.project_id,
    panorama_id: hotspot.panorama_id,
    name: hotspot.name,
    type: hotspot.type,
    content: hotspot.content,
    position: hotspot.position,
    rotation: hotspot.rotation,
    scale: hotspot.scale,
    visible: hotspot.visible,
    order: hotspot.order,
    cms_action: (hotspot as { cms_action?: unknown }).cms_action,
    cmsAction: (hotspot as { cmsAction?: unknown }).cmsAction,
  });

  const buildPanoramaSnapshot = (panorama: Panorama): PanoramaSnapshot => ({
    id: panorama.id,
    project_id: panorama.project_id,
    name: panorama.name,
    order: panorama.order,
    width: panorama.width,
    height: panorama.height,
    file_size: panorama.file_size,
    file_url: panorama.file_url,
    private_file_url: panorama.private_file_url,
    thumbnail_url: panorama.thumbnail_url,
    thumbnail_url_webp: panorama.thumbnail_url_webp,
    file_status: panorama.file_status,
    processing_status: panorama.processing_status,
    gps_location: panorama.gps_location,
    data: panorama.data,
  });

  const cloneValue = <T>(value: T): T => {
    if (value === null || value === undefined) return value;
    if (typeof structuredClone === "function") {
      return structuredClone(value);
    }
    try {
      return JSON.parse(JSON.stringify(value)) as T;
    } catch {
      return value;
    }
  };

  const cloneSnapshot = (snapshot: HotspotSnapshot): HotspotSnapshot => ({
    ...snapshot,
    content: cloneValue(snapshot.content),
    position: cloneValue(snapshot.position),
    rotation: cloneValue(snapshot.rotation),
    scale: cloneValue(snapshot.scale),
    cms_action: cloneValue(snapshot.cms_action),
    cmsAction: cloneValue(snapshot.cmsAction),
  });

  const clonePanoramaSnapshot = (snapshot: PanoramaSnapshot): PanoramaSnapshot => ({
    ...snapshot,
    gps_location: cloneValue(snapshot.gps_location),
    data: cloneValue(snapshot.data),
  });

  const cloneSkinComponent = (component: SkinComponent): SkinComponentSnapshot => ({
    ...(component as SkinComponentSnapshot),
    position: cloneValue(component.position),
    size: cloneValue(component.size),
    style: cloneValue(component.style),
    content: cloneValue(component.content),
  });

  const jsonEquals = (left: unknown, right: unknown) => {
    if (left === right) return true;
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return false;
    }
  };

  const snapshotsEqual = (left: HotspotSnapshot, right: HotspotSnapshot) =>
    left.id === right.id &&
    left.project_id === right.project_id &&
    left.panorama_id === right.panorama_id &&
    left.name === right.name &&
    left.type === right.type &&
    jsonEquals(left.content, right.content) &&
    jsonEquals(left.position, right.position) &&
    jsonEquals(left.rotation, right.rotation) &&
    jsonEquals(left.scale, right.scale) &&
    jsonEquals(left.cms_action, right.cms_action) &&
    jsonEquals(left.cmsAction, right.cmsAction) &&
    left.visible === right.visible &&
    left.order === right.order;

  const ensureHotspotKey = (hotspotId: string) => {
    const state = get();
    const existingKey = state.hotspotKeyById[hotspotId];
    if (existingKey) return existingKey;
    const key = hotspotId;
    set((current) => ({
      hotspotKeyById: { ...current.hotspotKeyById, [hotspotId]: key },
      hotspotIdByKey: { ...current.hotspotIdByKey, [key]: hotspotId },
    }));
    return key;
  };

  const updateHotspotKeyMapping = (key: string, hotspotId: string | null) => {
    set((current) => {
      const nextKeyById = { ...current.hotspotKeyById };
      const nextIdByKey = { ...current.hotspotIdByKey };
      const previousId = nextIdByKey[key];
      if (previousId) {
        delete nextKeyById[previousId];
      }
      if (hotspotId) {
        nextKeyById[hotspotId] = key;
      }
      nextIdByKey[key] = hotspotId ?? null;
      return {
        hotspotKeyById: nextKeyById,
        hotspotIdByKey: nextIdByKey,
      };
    });
  };

  const pushHistoryAction = (action: EditorHistoryActionWithSkin) => {
    set((state) => {
      if (state.isApplyingHistory) return state;
      const nextUndo = [...state.undoStack, action];
      if (nextUndo.length > 50) {
        nextUndo.shift();
      }
      return { undoStack: nextUndo, redoStack: [] };
    });
  };

  const applyHotspotUpdate = async (hotspotId: string, snapshot: HotspotSnapshot) => {
    const updates: Partial<Hotspot> = {
      name: snapshot.name,
      type: snapshot.type,
      content: snapshot.content,
      position: snapshot.position,
      rotation: snapshot.rotation,
      scale: snapshot.scale,
      visible: snapshot.visible,
      order: snapshot.order,
      cms_action: snapshot.cms_action,
      cmsAction: snapshot.cmsAction,
    };
    const dbUpdates: Partial<Hotspot> = {
      name: snapshot.name,
      type: snapshot.type,
      content: snapshot.content,
      position: snapshot.position,
      rotation: snapshot.rotation,
      scale: snapshot.scale,
      visible: snapshot.visible,
      order: snapshot.order,
    };

    if (shouldUseLocalBackend(get())) {
      const craftspaceId = get().project?.craftspace_id || "default";
      await updateLocalHotspot({
        id: hotspotId,
        craftspace_id: craftspaceId,
        ...(dbUpdates as Parameters<typeof updateLocalHotspot>[0]),
      });
    } else {
      await convex.mutation(api.hotspots.update, buildHotspotUpdateArgs(hotspotId, dbUpdates));
    }

    set((state) => ({
      hotspots: state.hotspots.map((h) =>
        h.id === hotspotId ? ({ ...h, ...updates } as Hotspot) : h,
      ),
      selectedHotspot:
        state.selectedHotspot?.id === hotspotId
          ? ({ ...state.selectedHotspot, ...updates } as Hotspot)
          : state.selectedHotspot,
    }));
  };

  const applyPanoramaUpdate = async (panoramaId: string, snapshot: PanoramaSnapshot) => {
    const updates: Partial<Panorama> = {
      name: snapshot.name,
      order: snapshot.order,
      width: snapshot.width,
      height: snapshot.height,
      file_size: snapshot.file_size,
      file_url: snapshot.file_url,
      private_file_url: snapshot.private_file_url,
      thumbnail_url: snapshot.thumbnail_url,
      thumbnail_url_webp: snapshot.thumbnail_url_webp,
      file_status: snapshot.file_status,
      processing_status: snapshot.processing_status,
      gps_location: snapshot.gps_location,
      data: snapshot.data,
    };

    if (shouldUseLocalBackend(get())) {
      const craftspaceId = get().project?.craftspace_id || "default";
      await updateLocalPanorama({
        id: panoramaId,
        craftspace_id: craftspaceId,
        ...(updates as Parameters<typeof updateLocalPanorama>[0]),
      });
    } else {
      await convex.mutation(api.panoramas.update, buildPanoramaUpdateArgs(panoramaId, updates));
    }

    set((state) => ({
      panoramas: state.panoramas.map((p) => (p.id === panoramaId ? { ...p, ...updates } : p)),
      selectedPanorama:
        state.selectedPanorama?.id === panoramaId
          ? { ...state.selectedPanorama, ...updates }
          : state.selectedPanorama,
    }));
  };

  const applySkinComponentCreate = (component: SkinComponentSnapshot, index?: number) => {
    set((state) => {
      const next = [...state.skinComponents];
      const insertIndex =
        index !== undefined ? Math.max(0, Math.min(index, next.length)) : next.length;
      next.splice(insertIndex, 0, component);
      return {
        skinComponents: next,
        selectedSkinComponent: component,
        isDirty: true,
      };
    });
  };

  const applySkinComponentDelete = (componentId: string) => {
    set((state) => ({
      skinComponents: state.skinComponents.filter((c) => c.id !== componentId),
      selectedSkinComponent:
        state.selectedSkinComponent?.id === componentId ? null : state.selectedSkinComponent,
      isDirty: true,
    }));
  };

  const applySkinComponentUpdate = (componentId: string, snapshot: SkinComponentSnapshot) => {
    set((state) => ({
      skinComponents: state.skinComponents.map((c) => (c.id === componentId ? snapshot : c)),
      selectedSkinComponent:
        state.selectedSkinComponent?.id === componentId ? snapshot : state.selectedSkinComponent,
      isDirty: true,
    }));
  };

  const applySkinComponentsReplace = (components: SkinComponentSnapshot[]) => {
    set((state) => ({
      skinComponents: components,
      selectedSkinComponent: state.selectedSkinComponent
        ? components.find((c) => c.id === state.selectedSkinComponent?.id) || null
        : state.selectedSkinComponent,
      isDirty: true,
    }));
  };

  const applyHotspotDelete = async (hotspotId: string, key: string) => {
    if (shouldUseLocalBackend(get())) {
      const craftspaceId = get().project?.craftspace_id || "default";
      await removeLocalHotspot(hotspotId, craftspaceId);
    } else {
      await convex.mutation(api.hotspots.remove, { id: hotspotId });
    }

    set((state) => ({
      hotspots: state.hotspots.filter((h) => h.id !== hotspotId),
      selectedHotspot: state.selectedHotspot?.id === hotspotId ? null : state.selectedHotspot,
    }));

    updateHotspotKeyMapping(key, null);
  };

  const applyHotspotCreate = async (snapshot: HotspotSnapshot, key: string) => {
    const craftspaceId = get().project?.craftspace_id || "default";
    const createParams = {
      project_id: snapshot.project_id,
      craftspace_id: craftspaceId,
      panorama_id: snapshot.panorama_id,
      name: snapshot.name,
      type: snapshot.type,
      content: snapshot.content,
      position: snapshot.position,
      rotation: snapshot.rotation,
      scale: snapshot.scale,
      visible: snapshot.visible,
      order: snapshot.order,
    };

    let hotspotData: Hotspot;
    if (shouldUseLocalBackend(get())) {
      const id = await createLocalHotspot(createParams);
      const pos = (snapshot.position || { x: 0, y: 0, z: 0 }) as {
        x: number;
        y: number;
        z: number;
      };
      hotspotData = {
        ...(createParams as Hotspot),
        id,
        _id: id,
        position_x: pos.x,
        position_y: pos.y,
        position_z: pos.z,
      } as Hotspot;
    } else {
      hotspotData = (await convex.mutation(api.hotspots.create, createParams)) as Hotspot;
    }

    set((state) => ({
      hotspots: [...state.hotspots, hotspotData],
      selectedHotspot: hotspotData,
    }));

    updateHotspotKeyMapping(key, hotspotData.id);

    return hotspotData;
  };

  return {
    // Initial state
    project: null,
    panoramas: [],
    hotspots: [],
    skinComponents: [],

    selectedPanorama: null,
    selectedPanoramaIds: [],
    selectedHotspot: null,
    selectedHotspotIds: [],
    selectedSkinComponent: null,
    placementMode: false,
    placementType: "svg" as "svg" | "html" | "video",
    htmlPlacementContentType: "icon",
    isDraggingToAddHotspot: false, // Track what type of hotspot we're placing
    pendingHotspotPlacementId: null,

    deletedHotspots: [],
    hotspotMovementUndo: null,
    undoStack: [],
    redoStack: [],
    hotspotKeyById: {},
    hotspotIdByKey: {},
    isApplyingHistory: false,

    loading: true,
    localLoading: true,
    isSaving: false,
    isDirty: false,
    isPanelSwitching: false,

    // UI State
    activeTab: "hotspots",
    hoveredHotspot: null,
    isControlsActive: true,
    placementPreview: null,
    hotspotTooltip: null,
    livePosition: null,
    showPanoramaProperties: false,
    chromaKeyPickHotspotId: null,
    hotspotPanelTab: null,
    hotspotTextAutoFocus: false,

    // Viewer Feature Flags
    enableHotspotPlacement: true,
    enableHotspotEditing: true,
    enableSkinComponentEditing: true,
    showPlacementButton: true,
    currentRoute: "editor",

    // CMS initial state
    activeCMSDatasetId: null,
    activeCMSRowIndex: 0,
    resolvedBindings: {},
    currentCMSRowData: null,
    viewerVisible: true,
    isPreviewMode: false,
    hotspotRenderStyle: "billboard" as const,
    isAspectRatioLocked: true, // Locked by default
    skinViewportWidth: null,
    skinViewportPreset: null,

    // Camera view state
    currentCameraView: null,
    defaultViewSaveEvent: null,
    pendingPanoramaView: null,
    focusHotspotRequestId: null,
    projectBackend: null,

    // Actions
    setProject: (project) => set({ project }),
    setLoading: (loading) => set({ loading }),
    setLocalLoading: (localLoading) => set({ localLoading }),
    setIsSaving: (isSaving) => set({ isSaving }),
    setIsDirty: (isDirty) => set({ isDirty }),
    setIsPanelSwitching: (isPanelSwitching) => set({ isPanelSwitching }),

    setPanoramas: (panoramas) => set({ panoramas: Array.isArray(panoramas) ? panoramas : [] }),
    setHotspots: (hotspots) =>
      set((state) => {
        const nextHotspots = Array.isArray(hotspots) ? hotspots : [];
        const nextSelectedHotspot = state.selectedHotspot
          ? nextHotspots.find((hotspot) => hotspot.id === state.selectedHotspot?.id) || null
          : state.selectedHotspot;
        const nextSelectedHotspotIds = state.selectedHotspotIds.filter((id) =>
          nextHotspots.some((hotspot) => hotspot.id === id),
        );

        return {
          hotspots: nextHotspots,
          selectedHotspot: nextSelectedHotspot,
          selectedHotspotIds: nextSelectedHotspotIds,
        };
      }),
    setSkinComponents: (skinComponents) => set({ skinComponents }),

    refreshPanoramas: async (projectId: string) => {
      try {
        const panoramasData = (await convex.query(api.panoramas.listByProject, {
          projectId,
        })) as Panorama[];

        // Add default initialView to panoramas that don't have it
        const panoramasWithDefaults = (panoramasData || []).map((panorama) => {
          if (!panorama.data?.initialView) {
            return {
              ...panorama,
              data: {
                ...panorama.data,
                initialView: {
                  position: { x: 0, y: 0, z: 0 },
                  target: { x: 1, y: 0, z: 0 },
                  fov: 75,
                },
              },
            };
          }
          return panorama;
        });

        set({ panoramas: panoramasWithDefaults });

        // Update selected panorama if the current one is no longer in the list
        const current = get();
        if (
          current.selectedPanorama &&
          !panoramasWithDefaults?.find((p) => p.id === current.selectedPanorama?.id)
        ) {
          set({ selectedPanorama: panoramasWithDefaults?.[0] || null });
        }

        return panoramasWithDefaults;
      } catch (error) {
        console.error("Failed to refresh panoramas:", error);
        throw error;
      }
    },

    // Update existing panoramas that don't have initialView in the database
    // This function can be called to fix existing panoramas that don't have default views
    updatePanoramasWithDefaultInitialView: async () => {
      try {
        // Get all panoramas for the current user
        const panoramasData = (await convex.query(api.panoramas.listMine, {})) as Panorama[];
        const panoramasMissingInitialView = (panoramasData || []).filter(
          (panorama) => !panorama.data?.initialView,
        );

        if (!panoramasMissingInitialView || panoramasMissingInitialView.length === 0) {
          console.log("✅ All panoramas already have initialView");
          return;
        }

        console.log(
          `🔄 Updating ${panoramasMissingInitialView.length} panoramas with default initialView`,
        );

        // Update panoramas that don't have initialView
        const updatePromises = panoramasMissingInitialView.map(async (panorama) => {
          const updatedData = {
            ...panorama.data,
            initialView: {
              position: { x: 0, y: 0, z: 1 },
              target: { x: 0, y: 0, z: 0 },
              fov: 75,
            },
          };

          try {
            await convex.mutation(api.panoramas.update, { id: panorama.id, data: updatedData });
            console.log(`✅ Updated initialView for panorama ${panorama.name}`);
          } catch (updateError) {
            console.warn(
              `Failed to update initialView for panorama ${panorama.name}:`,
              updateError,
            );
          }
        });

        await Promise.all(updatePromises);
        console.log("✅ Finished updating panoramas with default initialView");
      } catch (error) {
        console.error("Failed to update panoramas with default initialView:", error);
      }
    },

    // Discover panoramas directly from storage files (flat project folder)
    discoverPanoramasFromStorage: async (craftspaceId: string, projectId: string) => {
      // Using static import (already imported at top)
      try {
        const basePath = `${craftspaceId}/${projectId}`;
        const thumbnailsPath = `${basePath}/auto-generated`;
        const { data: rootItems } = await StorageService.listFiles("staging", basePath);

        let thumbnailItems: StorageEntry[] = [];
        try {
          const { data } = await StorageService.listFiles("staging", thumbnailsPath);
          thumbnailItems = data || [];
        } catch (_error) {
          thumbnailItems = [];
        }

        const discoveredPanoramas: Panorama[] = [];
        const panoramaMap = new Map<
          string,
          { main?: string; thumbnail?: string; thumbnailWebp?: string }
        >();

        const getEntryName = (entry: StorageEntry & Record<string, unknown>) =>
          entry.name || entry.id || (entry as Record<string, unknown>).ObjectName || (entry as Record<string, unknown>).Name;

        const isImageFile = (name: string) =>
          name.endsWith(".jpg") ||
          name.endsWith(".jpeg") ||
          name.endsWith(".png") ||
          name.endsWith(".webp");

        const extractPanoramaName = (name: string) => {
          const baseName = name.split("/").pop() || name;
          if (baseName.includes("__")) return baseName.split("__")[0];
          return baseName.replace(/\.[^/.]+$/, "");
        };

        for (const item of rootItems || []) {
          const entryName = getEntryName(item as StorageEntry & Record<string, unknown>);
          if (!entryName || String(entryName).startsWith(".")) continue;
          if (entryName === "auto-generated" || entryName === "trash") continue;
          if (!isImageFile(entryName)) continue;
          if (
            entryName.startsWith("hotspot_") ||
            entryName.startsWith("skin_") ||
            entryName.startsWith("cms_") ||
            entryName.startsWith("export_") ||
            entryName.startsWith("version_")
          ) {
            continue;
          }

          const panoramaName = extractPanoramaName(entryName);
          const entry = panoramaMap.get(panoramaName) || {};
          if (!entry.main) {
            entry.main = entryName;
          }
          panoramaMap.set(panoramaName, entry);
        }

        for (const item of thumbnailItems || []) {
          const entryName = getEntryName(item as StorageEntry & Record<string, unknown>);
          if (!entryName || String(entryName).startsWith(".")) continue;
          if (!isImageFile(entryName)) continue;
          const panoramaName = extractPanoramaName(entryName);
          const entry = panoramaMap.get(panoramaName) || {};

          if (entryName.includes("compressed_")) {
            entry.main = entryName;
          } else if (entryName.includes("thumbnail") && entryName.endsWith(".jpg")) {
            entry.thumbnail = entryName;
          } else if (entryName.includes("thumbnail") && entryName.endsWith(".webp")) {
            entry.thumbnailWebp = entryName;
          }
          panoramaMap.set(panoramaName, entry);
        }

        for (const [panoramaName, files] of panoramaMap.entries()) {
          const existingPanorama = get().panoramas.find((p) => p.name === panoramaName);
          if (existingPanorama) {
            discoveredPanoramas.push(existingPanorama);
            continue;
          }

          if (!files.main) {
            console.warn(`⚠️ No main panorama file found for ${panoramaName}`);
            continue;
          }

          const mainPath = files.main.includes("compressed_")
            ? `${thumbnailsPath}/${files.main}`
            : `${basePath}/${files.main}`;
          const thumbnailPath = files.thumbnail ? `${thumbnailsPath}/${files.thumbnail}` : null;
          const thumbnailWebpPath = files.thumbnailWebp
            ? `${thumbnailsPath}/${files.thumbnailWebp}`
            : null;

          let privateFileUrl = "";
          let thumbnailUrl = null;
          let thumbnailWebpUrl = null;

          try {
            const {
              data: { signedUrl },
            } = await StorageService.getPrivateUrl("staging", mainPath, 3600);
            privateFileUrl = signedUrl;

            if (thumbnailPath) {
              const {
                data: { signedUrl: thumbUrl },
              } = await StorageService.getPrivateUrl("staging", thumbnailPath, 3600);
              thumbnailUrl = thumbUrl;
            }

            if (thumbnailWebpPath) {
              const {
                data: { signedUrl: thumbWebpUrl },
              } = await StorageService.getPrivateUrl("staging", thumbnailWebpPath, 3600);
              thumbnailWebpUrl = thumbWebpUrl;
            }
          } catch (urlError) {
            console.warn(`Failed to generate signed URLs for ${panoramaName}:`, urlError);
          }

          const discoveredPanorama = {
            id: `discovered_${panoramaName}_${Date.now()}`,
            project_id: projectId,
            user_id: "",
            name: panoramaName,
            file_url: privateFileUrl,
            private_file_url: privateFileUrl,
            thumbnail_url: thumbnailUrl,
            thumbnail_url_webp: thumbnailWebpUrl,
            width: null,
            height: null,
            file_size: null,
            order: get().panoramas.length + discoveredPanoramas.length,
            file_status: "public",
            processing_status: "completed",
            created_at: new Date().toISOString(),
            isDiscovered: true,
            data: {
              storage_path: basePath,
              main_file: files.main,
              thumbnail_file: files.thumbnail,
              thumbnail_webp_file: files.thumbnailWebp,
            },
          };

          discoveredPanoramas.push(discoveredPanorama);
        }

        // Update the store with discovered panoramas
        set({ panoramas: discoveredPanoramas });

        // Set the first panorama as selected if none is currently selected
        const current = get();
        if (!current.selectedPanorama && discoveredPanoramas.length > 0) {
          set({ selectedPanorama: discoveredPanoramas[0] });
        }

        return discoveredPanoramas;
      } catch (error) {
        console.error("Failed to discover panoramas from storage:", error);
        throw error;
      }
    },

    // Sync discovered panoramas with database
    syncDiscoveredPanoramas: async (_craftspaceId: string, projectId: string) => {
      // Using static import (already imported at top)
      try {
        // console.log('🔄 Syncing discovered panoramas with database...');

        const currentPanoramas = get().panoramas;
        const discoveredPanoramas = currentPanoramas.filter((p) => p.isDiscovered);

        if (discoveredPanoramas.length === 0) {
          // console.log('✅ No discovered panoramas to sync');
          return [];
        }

        // console.log(`🔄 Syncing ${discoveredPanoramas.length} discovered panoramas...`);

        const existingPanoramas = (await convex.query(api.panoramas.listByProject, {
          projectId,
        })) as Panorama[];
        const existingPanoramaByName = new Map(existingPanoramas.map((p) => [p.name, p]));

        // Backfill missing initialView on existing panoramas.
        const updatePromises = existingPanoramas
          .filter((panorama) => !panorama.data?.initialView)
          .map(async (existingPanorama) => {
            const updatedData = {
              ...existingPanorama.data,
              initialView: {
                position: { x: 0, y: 0, z: 0 },
                target: { x: 1, y: 0, z: 0 },
                fov: 75,
              },
            };
            await convex.mutation(api.panoramas.update, {
              id: existingPanorama.id,
              data: updatedData,
            });
            return { ...existingPanorama, data: updatedData };
          });
        const updatedExistingPanoramas = await Promise.all(updatePromises);
        for (const panorama of updatedExistingPanoramas) {
          existingPanoramaByName.set(panorama.name, panorama);
        }

        const syncedPanoramas = [];

        for (const panorama of discoveredPanoramas) {
          try {
            // console.log(`🔄 Syncing panorama: ${panorama.name}`);

            // Check if panorama already exists in database
            const existingPanorama = existingPanoramaByName.get(panorama.name);

            if (existingPanorama) {
              console.log(`📋 Panorama ${panorama.name} already exists in database`);
              syncedPanoramas.push(existingPanorama);
              continue;
            }

            // Create new database record
            const panoramaDataWithDefaultView = {
              ...panorama.data,
              // Add default initial view if not already present
              initialView: panorama.data?.initialView || {
                position: { x: 0, y: 0, z: 1 },
                target: { x: 0, y: 0, z: 0 },
                fov: 75,
              },
            };

            const newPanorama = (await convex.mutation(api.panoramas.create, {
              project_id: projectId,
              name: panorama.name,
              file_url: panorama.file_url ?? undefined,
              private_file_url: panorama.private_file_url ?? undefined,
              thumbnail_url: panorama.thumbnail_url ?? undefined,
              thumbnail_url_webp: panorama.thumbnail_url_webp ?? undefined,
              width: panorama.width ?? undefined,
              height: panorama.height ?? undefined,
              file_size: panorama.file_size ?? undefined,
              order: panorama.order ?? undefined,
              file_status: "public",
              processing_status: "completed",
              data: panoramaDataWithDefaultView,
            })) as Panorama;

            console.log(`✅ Successfully created panorama ${panorama.name} in database`);
            syncedPanoramas.push(newPanorama);
            existingPanoramaByName.set(newPanorama.name, newPanorama);
          } catch (error) {
            console.error(`❌ Error syncing panorama ${panorama.name}:`, error);
          }
        }

        // console.log(`✅ Synced ${syncedPanoramas.length} panoramas with database`);

        // Update store with synced panoramas
        const nonDiscoveredPanoramas = currentPanoramas.filter((p) => !p.isDiscovered);
        const updatedPanoramas = [...nonDiscoveredPanoramas, ...syncedPanoramas];
        set({ panoramas: updatedPanoramas });

        return syncedPanoramas;
      } catch (error) {
        console.error("Failed to sync discovered panoramas:", error);
        throw error;
      }
    },

    addHotspot: (hotspot) => set((state) => ({ hotspots: [hotspot, ...state.hotspots] })),
    updateHotspot: (id, updates) =>
      set((state) => {
        const updatedHotspots = state.hotspots.map((h) =>
          h.id === id ? ({ ...h, ...updates } as Hotspot) : h,
        );
        const updatedSelectedHotspot =
          state.selectedHotspot?.id === id
            ? ({ ...state.selectedHotspot, ...updates } as Hotspot)
            : state.selectedHotspot;

        return {
          hotspots: updatedHotspots,
          selectedHotspot: updatedSelectedHotspot,
        };
      }),
    deleteHotspot: (id) =>
      set((state) => ({ hotspots: state.hotspots.filter((h) => h.id !== id) })),

    duplicateHotspot: async (hotspot) => {
      const state = get();

      if (!state.project || !state.selectedPanorama) {
        throw new Error("No project or panorama selected");
      }

      try {
        // Generate duplicate hotspot name
        const name = hotspot.name || "Hotspot";
        const numberMatch = name.match(/^(.*?)(\d+)\s*$/);
        const hotspotName = numberMatch
          ? `${numberMatch[1]}${Number(numberMatch[2]) + 1}`
          : `${name} Copy`;

        // Create duplicated hotspot with new ID and slightly offset position
        const mediaType = getPanoramaMediaType(state.selectedPanorama);
        const basePosition = (hotspot.position as { x?: number; y?: number; z?: number }) || {};
        const duplicatedHotspot = {
          name: hotspotName,
          position:
            mediaType === "zoomable_image"
              ? {
                  x: Math.min(1, (basePosition.x ?? 0.5) + 0.03),
                  y: Math.min(1, (basePosition.y ?? 0.5) + 0.03),
                  z: 0,
                }
              : {
                  x: (basePosition.x ?? 0) + 0.1, // Small offset to avoid exact overlap
                  y: (basePosition.y ?? 0) + 0.1,
                  z: (basePosition.z ?? 0) + 0.1,
                },
          type: hotspot.type,
          content: hotspot.content,
          rotation: hotspot.rotation,
          scale: hotspot.scale,
          visible: hotspot.visible,
          order: state.hotspots.filter((h) => h.panorama_id === state.selectedPanorama?.id).length,
        };

        // Insert into database
        const createParams = {
          project_id: state.project.id,
          craftspace_id: state.project.craftspace_id || "default",
          panorama_id: state.selectedPanorama.id,
          ...duplicatedHotspot,
        };
        let hotspotData: Hotspot;
        if (shouldUseLocalBackend(state)) {
          const id = await createLocalHotspot(createParams);
          const pos = createParams.position as { x: number; y: number; z: number } | undefined;
          hotspotData = {
            ...createParams,
            id,
            _id: id,
            position_x: pos?.x ?? 0,
            position_y: pos?.y ?? 0,
            position_z: pos?.z ?? 0,
          } as Hotspot;
        } else {
          hotspotData = (await convex.mutation(api.hotspots.create, createParams)) as Hotspot;
        }

        // Update store
        set((state) => ({
          hotspots: [hotspotData, ...state.hotspots],
          selectedHotspot: hotspotData,
        }));
      } catch (error) {
        console.error("Error duplicating hotspot:", error);
        throw error;
      }
    },

    setSelectedPanorama: (panorama) =>
      set((state) => {
        if (!panorama) {
          return { selectedPanorama: null, selectedPanoramaIds: [] };
        }
        const nextPanoramas = state.panoramas.map((p) =>
          p.id === panorama.id && !p.isDiscovered ? { ...p, isDiscovered: true } : p,
        );
        const nextSelected =
          nextPanoramas.find((p) => p.id === panorama.id) || panorama;
        return {
          panoramas: nextPanoramas,
          selectedPanorama: nextSelected,
          selectedPanoramaIds: [panorama.id],
        };
      }),
    setSelectedPanoramaIds: (ids) => set({ selectedPanoramaIds: ids }),
    setSelectedHotspot: (hotspot) => set({ selectedHotspot: hotspot }),
    setSelectedHotspotIds: (ids) => set({ selectedHotspotIds: ids }),
    toggleHotspotSelection: (id) =>
      set((state) => {
        const isSelected = state.selectedHotspotIds.includes(id);
        if (isSelected) {
          return {
            selectedHotspotIds: state.selectedHotspotIds.filter((selectedId) => selectedId !== id),
          };
        } else {
          return { selectedHotspotIds: [...state.selectedHotspotIds, id] };
        }
      }),
    clearHotspotSelection: () => set({ selectedHotspotIds: [] }),
    selectAllHotspots: () =>
      set((state) => {
        const currentPanorama = state.selectedPanorama;
        if (!currentPanorama) return state;

        const panoramaHotspots = state.hotspots
          .filter((h) => h.panorama_id === currentPanorama.id)
          .map((h) => h.id);

        return { selectedHotspotIds: panoramaHotspots };
      }),
    setSelectedSkinComponent: (component) => {
      set({ selectedSkinComponent: component });
    },

    setPlacementMode: (placementMode) => set({ placementMode }),
    setPlacementType: (placementType) => set({ placementType }),
    setHtmlPlacementContentType: (htmlPlacementContentType) => set({ htmlPlacementContentType }),
    setIsDraggingToAddHotspot: (isDraggingToAddHotspot) => set({ isDraggingToAddHotspot }),
    setPendingHotspotPlacementId: (pendingHotspotPlacementId) =>
      set({ pendingHotspotPlacementId }),

    pushDeletedHotspot: (hotspot) =>
      set((state) => ({ deletedHotspots: [hotspot, ...state.deletedHotspots] })),
    popDeletedHotspot: () => {
      let popped: Hotspot | undefined;
      set((state) => {
        const [first, ...rest] = state.deletedHotspots;
        popped = first;
        return { deletedHotspots: rest };
      });
      return popped;
    },

    setHotspotMovementUndo: (undo) => set({ hotspotMovementUndo: undo }),
    recordHotspotCreate: (hotspot) => {
      if (get().isApplyingHistory) return;
      const key = ensureHotspotKey(hotspot.id);
      const snapshot = cloneSnapshot(buildHotspotSnapshot(hotspot));
      pushHistoryAction({ type: "create", key, hotspot: snapshot });
    },
    recordHotspotDelete: (hotspot) => {
      if (get().isApplyingHistory) return;
      const key = ensureHotspotKey(hotspot.id);
      const snapshot = cloneSnapshot(buildHotspotSnapshot(hotspot));
      pushHistoryAction({ type: "delete", key, hotspot: snapshot });
    },
    recordHotspotUpdate: (hotspotId, updates, options) => {
      if (get().isApplyingHistory) return;
      const state = get();
      const hotspot = state.hotspots.find((h) => h.id === hotspotId);
      if (!hotspot) return;
      const snapshotCurrent = buildHotspotSnapshot(hotspot);
      const before = options?.beforeOverride
        ? ({ ...snapshotCurrent, ...options.beforeOverride } as HotspotSnapshot)
        : snapshotCurrent;
      const after = { ...snapshotCurrent, ...updates } as HotspotSnapshot;
      const safeBefore = cloneSnapshot(before);
      const safeAfter = cloneSnapshot(after);
      if (snapshotsEqual(safeBefore, safeAfter)) return;
      const key = ensureHotspotKey(hotspotId);
      pushHistoryAction({ type: "update", key, before: safeBefore, after: safeAfter });
    },
    recordHotspotUpdateWithSnapshots: (hotspotId, before, after) => {
      if (get().isApplyingHistory) return;
      const safeBefore = cloneSnapshot(before);
      const safeAfter = cloneSnapshot(after);
      if (snapshotsEqual(safeBefore, safeAfter)) return;
      const key = ensureHotspotKey(hotspotId);
      pushHistoryAction({ type: "update", key, before: safeBefore, after: safeAfter });
    },
    recordPanoramaUpdate: (panoramaId, updates, options) => {
      if (get().isApplyingHistory) return;
      const state = get();
      const panorama = state.panoramas.find((p) => p.id === panoramaId);
      if (!panorama) return;
      const snapshotCurrent = buildPanoramaSnapshot(panorama);
      const before = options?.beforeOverride
        ? ({ ...snapshotCurrent, ...options.beforeOverride } as PanoramaSnapshot)
        : snapshotCurrent;
      const after = { ...snapshotCurrent, ...updates } as PanoramaSnapshot;
      const safeBefore = clonePanoramaSnapshot(before);
      const safeAfter = clonePanoramaSnapshot(after);
      if (jsonEquals(safeBefore, safeAfter)) return;
      pushHistoryAction({
        type: "panorama_update",
        panoramaId,
        before: safeBefore,
        after: safeAfter,
      });
    },
    recordSkinComponentCreate: (component, index) => {
      if (get().isApplyingHistory) return;
      const safeComponent = cloneSkinComponent(component);
      const insertIndex = index ?? get().skinComponents.length;
      pushHistoryAction({ type: "skin_create", component: safeComponent, index: insertIndex });
    },
    recordSkinComponentDelete: (component, index) => {
      if (get().isApplyingHistory) return;
      const safeComponent = cloneSkinComponent(component);
      pushHistoryAction({ type: "skin_delete", component: safeComponent, index });
    },
    recordSkinComponentUpdate: (componentId, updates, options) => {
      if (get().isApplyingHistory) return;
      const state = get();
      const component = state.skinComponents.find((c) => c.id === componentId);
      if (!component) return;
      const snapshotCurrent = cloneSkinComponent(component);
      const before = options?.beforeOverride
        ? ({ ...snapshotCurrent, ...options.beforeOverride } as SkinComponentSnapshot)
        : snapshotCurrent;
      const after = { ...snapshotCurrent, ...updates } as SkinComponentSnapshot;
      const safeBefore = cloneSkinComponent(before);
      const safeAfter = cloneSkinComponent(after);
      if (jsonEquals(safeBefore, safeAfter)) return;
      pushHistoryAction({
        type: "skin_update",
        componentId,
        before: safeBefore,
        after: safeAfter,
      });
    },
    recordSkinComponentsReorder: (before, after) => {
      if (get().isApplyingHistory) return;
      const safeBefore = before.map(cloneSkinComponent);
      const safeAfter = after.map(cloneSkinComponent);
      if (jsonEquals(safeBefore, safeAfter)) return;
      pushHistoryAction({ type: "skin_reorder", before: safeBefore, after: safeAfter });
    },
    undoHotspotEdit: async () => {
      const state = get();
      const action = state.undoStack[state.undoStack.length - 1];
      if (!action) return;
      set({ isApplyingHistory: true, undoStack: state.undoStack.slice(0, -1) });
      try {
        if (action.type === "skin_reorder") {
          applySkinComponentsReplace(action.before);
        } else if (action.type === "skin_update") {
          applySkinComponentUpdate(action.componentId, action.before);
        } else if (action.type === "skin_delete") {
          applySkinComponentCreate(action.component, action.index);
        } else if (action.type === "skin_create") {
          applySkinComponentDelete(action.component.id);
        } else if (action.type === "panorama_update") {
          await applyPanoramaUpdate(action.panoramaId, action.before);
        } else if (action.type === "create") {
          const hotspotId = get().hotspotIdByKey[action.key];
          if (hotspotId) {
            await applyHotspotDelete(hotspotId, action.key);
          }
        } else if (action.type === "delete") {
          await applyHotspotCreate(action.hotspot, action.key);
        } else if (action.type === "update") {
          const hotspotId = get().hotspotIdByKey[action.key];
          if (hotspotId) {
            await applyHotspotUpdate(hotspotId, action.before);
          }
        }
        set((current) => ({ redoStack: [...current.redoStack, action] }));
      } finally {
        set({ isApplyingHistory: false });
      }
    },
    redoHotspotEdit: async () => {
      const state = get();
      const action = state.redoStack[state.redoStack.length - 1];
      if (!action) return;
      set({ isApplyingHistory: true, redoStack: state.redoStack.slice(0, -1) });
      try {
        if (action.type === "skin_reorder") {
          applySkinComponentsReplace(action.after);
        } else if (action.type === "skin_update") {
          applySkinComponentUpdate(action.componentId, action.after);
        } else if (action.type === "skin_delete") {
          applySkinComponentDelete(action.component.id);
        } else if (action.type === "skin_create") {
          applySkinComponentCreate(action.component, action.index);
        } else if (action.type === "panorama_update") {
          await applyPanoramaUpdate(action.panoramaId, action.after);
        } else if (action.type === "create") {
          await applyHotspotCreate(action.hotspot, action.key);
        } else if (action.type === "delete") {
          const hotspotId = get().hotspotIdByKey[action.key];
          if (hotspotId) {
            await applyHotspotDelete(hotspotId, action.key);
          }
        } else if (action.type === "update") {
          const hotspotId = get().hotspotIdByKey[action.key];
          if (hotspotId) {
            await applyHotspotUpdate(hotspotId, action.after);
          }
        }
        set((current) => ({ undoStack: [...current.undoStack, action] }));
      } finally {
        set({ isApplyingHistory: false });
      }
    },

    // UI State Actions
    setActiveTab: (tab) => set({ activeTab: tab }),
    setHoveredHotspot: (hoveredHotspot) => set({ hoveredHotspot }),
    setIsControlsActive: (isControlsActive) => set({ isControlsActive }),
    setPlacementPreview: (placementPreview) => set({ placementPreview }),
    setHotspotTooltip: (hotspotTooltip) => set({ hotspotTooltip }),
    setLivePosition: (livePosition) => set({ livePosition }),
    setShowPanoramaProperties: (showPanoramaProperties) => set({ showPanoramaProperties }),
    setChromaKeyPickHotspotId: (hotspotId) => set({ chromaKeyPickHotspotId: hotspotId }),
    setHotspotPanelTab: (hotspotPanelTab) => set({ hotspotPanelTab }),
    setHotspotTextAutoFocus: (hotspotTextAutoFocus) => set({ hotspotTextAutoFocus }),

    // Viewer Feature Flag Actions
    setEnableHotspotPlacement: (enable) => set({ enableHotspotPlacement: enable }),
    setEnableHotspotEditing: (enable) => set({ enableHotspotEditing: enable }),
    setEnableSkinComponentEditing: (enable) => set({ enableSkinComponentEditing: enable }),
    setShowPlacementButton: (show) => set({ showPlacementButton: show }),
    setCurrentRoute: (route) => set({ currentRoute: route }),

    // CMS actions
    setActiveCMSRecord: (datasetId, rowIndex) => {
      const currentState = get();
      console.log("📊 Setting active CMS record:", {
        previous: {
          datasetId: currentState.activeCMSDatasetId,
          rowIndex: currentState.activeCMSRowIndex,
        },
        new: {
          datasetId,
          rowIndex,
        },
      });

      set({
        activeCMSDatasetId: datasetId,
        activeCMSRowIndex: rowIndex,
      });

      console.log("✅ Active CMS record updated in store");
    },
    setResolvedBindings: (bindings) => set({ resolvedBindings: bindings }),
    setCurrentCMSRowData: (data) => set({ currentCMSRowData: data }),
    setViewerVisible: (visible) => set({ viewerVisible: visible }),
    setIsPreviewMode: (isPreview) => set({ isPreviewMode: isPreview }),
    setHotspotRenderStyle: (style) => set({ hotspotRenderStyle: style }),
    setSkinViewportState: ({ width, preset }) =>
      set({ skinViewportWidth: width, skinViewportPreset: preset }),
    setIsAspectRatioLocked: (locked) => set({ isAspectRatioLocked: locked }),

    // Skin Component Actions
    addSkinComponent: (type, additionalData) => {
      const getDefaultContent = (type: string) => {
        switch (type) {
          case "box":
            return {};
          case "text":
            return { text: "" };
          case "image":
            return { src: "", alt: "Image", objectFit: "cover" };
          case "icon":
            return { src: "", svg: "", alt: "Icon", objectFit: "contain" };
          case "tabs":
            return { tabs: [], activeTabId: "" };
          case "collection-list":
            return {
              dataSourceId: "",
              itemTemplate: [],
            };
          case "dropdown":
            return {
              options: ["Option 1", "Option 2"],
              selectedIndex: 0,
              placeholder: "Select option",
              dataSourceId: "",
              labelField: "name",
              actionType: "panorama",
              useManualOptions: false,
            };
          default:
            return {};
        }
      };

      const getDefaultSize = (type: string) => {
        switch (type) {
          case "box":
            return { width: "auto", height: "auto" };
          case "text":
            return { width: "auto", height: "auto" };
          case "image":
            return { width: 200, height: 200 };
          case "icon":
            return { width: 32, height: "auto", aspectRatio: "1" };
          case "tabs":
            return { width: "auto", height: "auto" };
          case "collection-list":
            return { width: 300, height: "auto" };
          case "dropdown":
            return { width: 180, height: 32 };
          default:
            return { width: "auto", height: "auto" };
        }
      };

      const getDefaultStyle = (type: string) => {
        switch (type) {
          case "box":
            return { backgroundColor: "rgba(255, 255, 255, 0.33)", borderRadius: 0 };
          case "text":
            return {
              color: "#ffffff",
              fontSize: "16px",
              fontFamily: "Arial, sans-serif",
              fontWeight: "normal",
              textAlign: "left",
              lineHeight: "1.4",
            };
          case "image":
            return {
              borderRadius: 0,
              objectFit: "cover",
            };
          case "icon":
            return {
              borderRadius: 0,
              objectFit: "contain",
            };
          case "tabs":
            return {
              backgroundColor: "transparent",
              borderRadius: 0,
            };
          case "collection-list":
            return {
              backgroundColor: "transparent",
              gap: "8px",
              flexDirection: "column",
            };
          case "dropdown":
            return {
              backgroundColor: "#1f2937",
              color: "#ffffff",
              borderColor: "#374151",
              borderWidth: 1,
              borderRadius: 6,
              padding: "6px 10px",
              fontSize: "12px",
              fontFamily: "Arial, sans-serif",
            };
          default:
            return { backgroundColor: "rgba(255, 255, 255, 0.33)", borderRadius: 0 };
        }
      };

      const generateComponentName = (type: string) => {
        if (type === "box") {
          const existingBoxes = get().skinComponents.filter((c) => c.type === "box");
          const boxNames = existingBoxes.map((c) => c.name);

          // Check if "Box" exists
          if (!boxNames.includes("Box")) {
            return "Box";
          }

          // Find the next available number
          let counter = 2;
          while (boxNames.includes(`Box ${counter}`)) {
            counter++;
          }

          return `Box ${counter}`;
        }

        if (type === "text") {
          const existingTexts = get().skinComponents.filter((c) => c.type === "text");
          const textNames = existingTexts.map((c) => c.name);

          // Check if "Text" exists
          if (!textNames.includes("Text")) {
            return "Text";
          }

          // Find the next available number
          let counter = 2;
          while (textNames.includes(`Text ${counter}`)) {
            counter++;
          }

          return `Text ${counter}`;
        }

        if (type === "image") {
          const existingImages = get().skinComponents.filter((c) => c.type === "image");
          const imageNames = existingImages.map((c) => c.name);

          // Check if "Image" exists
          if (!imageNames.includes("Image")) {
            return "Image";
          }

          // Find the next available number
          let counter = 2;
          while (imageNames.includes(`Image ${counter}`)) {
            counter++;
          }

          return `Image ${counter}`;
        }

        if (type === "icon") {
          const existingIcons = get().skinComponents.filter((c) => c.type === "icon");
          const iconNames = existingIcons.map((c) => c.name);

          // Check if "Icon" exists
          if (!iconNames.includes("Icon")) {
            return "Icon";
          }

          // Find the next available number
          let counter = 2;
          while (iconNames.includes(`Icon ${counter}`)) {
            counter++;
          }

          return `Icon ${counter}`;
        }

        if (type === "collection-list") {
          const existingLists = get().skinComponents.filter((c) => c.type === "collection-list");
          const listNames = existingLists.map((c) => c.name);

          // Check if "Collection" exists
          if (!listNames.includes("Collection")) {
            return "Collection";
          }

          // Find the next available number
          let counter = 2;
          while (listNames.includes(`Collection ${counter}`)) {
            counter++;
          }

          return `Collection ${counter}`;
        }

        if (type === "tabs") {
          const existingTabs = get().skinComponents.filter((c) => c.type === "tabs");
          const tabNames = existingTabs.map((c) => c.name);

          if (!tabNames.includes("Tabs")) {
            return "Tabs";
          }

          let counter = 2;
          while (tabNames.includes(`Tabs ${counter}`)) {
            counter++;
          }

          return `Tabs ${counter}`;
        }

        if (type === "dropdown") {
          const existingDropdowns = get().skinComponents.filter((c) => c.type === "dropdown");
          const dropdownNames = existingDropdowns.map((c) => c.name);

          if (!dropdownNames.includes("Dropdown")) {
            return "Dropdown";
          }

          let counter = 2;
          while (dropdownNames.includes(`Dropdown ${counter}`)) {
            counter++;
          }

          return `Dropdown ${counter}`;
        }

        // For other component types, use generic naming
        return `New ${type.charAt(0).toUpperCase() + type.slice(1)}`;
      };

      const defaultContent = getDefaultContent(type);
      const selectedParent = get().selectedSkinComponent;
      const isNestableContainer = (componentType: string) =>
        componentType === "box" || componentType === "collection-list" || componentType === "tabs";
      let parentId: string | undefined;

      if (selectedParent) {
        if (isNestableContainer(selectedParent.type)) {
          parentId = selectedParent.id;
        } else {
          // If a nested child is selected, add into its nearest nestable ancestor.
          let currentParentId = selectedParent.parentId;
          while (currentParentId) {
            const parentComponent = get().skinComponents.find((component) => component.id === currentParentId);
            if (!parentComponent) break;
            if (isNestableContainer(parentComponent.type)) {
              parentId = parentComponent.id;
              break;
            }
            currentParentId = parentComponent.parentId;
          }
        }
      }

      const newComponent = {
        id: crypto.randomUUID(),
        type,
        name: generateComponentName(type),
        parentId,
        position: {},
        size: getDefaultSize(type),
        style: getDefaultStyle(type),
        content: additionalData ? { ...defaultContent, ...additionalData } : defaultContent,
        visible: true,
        zIndex: 0,
        pointerEvents: true,
        escToHide: type === "box" ? false : undefined, // Only set for box components, disabled by default
      };

      if (type === "tabs") {
        const tab1Id = crypto.randomUUID();
        const tab2Id = crypto.randomUUID();
        const tabs = [
          { id: tab1Id, label: "Tab 1" },
          { id: tab2Id, label: "Tab 2" },
        ];
        const tabsComponent = {
          ...newComponent,
          content: {
            ...(newComponent.content || {}),
            tabs,
            activeTabId: tab1Id,
            tabStyle: {
              textColor: "rgba(255,255,255,0.8)",
              activeTextColor: "#ffffff",
              backgroundColor: "rgba(255,255,255,0.06)",
              activeBackgroundColor: "rgba(59,130,246,0.3)",
              fontSize: 12,
              fontWeight: 500,
              paddingX: 8,
              paddingY: 4,
              borderRadius: 6,
            },
          },
        };
        const tabContentBase = {
          type: "box",
          position: {},
          size: { width: "100%", height: "auto" },
          style: { backgroundColor: "transparent", borderRadius: 0 },
          content: {},
          visible: true,
          pointerEvents: true,
        };
        const tabContent1 = {
          id: tab1Id,
          name: "Tab Content 1",
          parentId: tabsComponent.id,
          zIndex: 2,
          ...tabContentBase,
        };
        const tabContent2 = {
          id: tab2Id,
          name: "Tab Content 2",
          parentId: tabsComponent.id,
          zIndex: 1,
          ...tabContentBase,
        };

        set((state) => ({
          skinComponents: [...state.skinComponents, tabsComponent, tabContent1, tabContent2],
          selectedSkinComponent: tabsComponent,
          isDirty: true,
        }));
        get().recordSkinComponentCreate(tabsComponent);
        get().recordSkinComponentCreate(tabContent1);
        get().recordSkinComponentCreate(tabContent2);
        return;
      }

      set((state) => ({
        skinComponents: [...state.skinComponents, newComponent],
        selectedSkinComponent: newComponent,
        isDirty: true,
      }));
      get().recordSkinComponentCreate(newComponent);
    },
    addSkinComponentWithData: (component) => {
      set((state) => ({
        skinComponents: [...state.skinComponents, component],
        selectedSkinComponent: component,
        isDirty: true,
      }));
      get().recordSkinComponentCreate(component);
    },
    updateSkinComponent: (id, updates) => {
      const existing = get().skinComponents.find((c) => c.id === id);
      const previous = existing ? cloneSkinComponent(existing) : null;
      set((state) => ({
        skinComponents: state.skinComponents.map((c) => (c.id === id ? { ...c, ...updates } : c)),
        selectedSkinComponent:
          state.selectedSkinComponent?.id === id
            ? { ...state.selectedSkinComponent, ...updates }
            : state.selectedSkinComponent,
        isDirty: true,
      }));
      if (previous) {
        get().recordSkinComponentUpdate(id, updates, { beforeOverride: previous });
      }
    },
    deleteSkinComponent: (id) => {
      const existing = get().skinComponents.find((c) => c.id === id);
      const index = get().skinComponents.findIndex((c) => c.id === id);
      set((state) => ({
        skinComponents: state.skinComponents.filter((c) => c.id !== id),
        selectedSkinComponent:
          state.selectedSkinComponent?.id === id ? null : state.selectedSkinComponent,
        isDirty: true,
      }));
      if (existing && index >= 0) {
        get().recordSkinComponentDelete(existing, index);
      }
    },
    reorderSkinComponents: (components) => {
      const before = get().skinComponents;
      set({
        skinComponents: components,
        isDirty: true,
      });
      get().recordSkinComponentsReorder(before, components);
    },
    nestSkinComponent: (childId, parentId) => {
      const existing = get().skinComponents.find((c) => c.id === childId);
      const previous = existing ? cloneSkinComponent(existing) : null;
      set((state) => ({
        skinComponents: state.skinComponents.map((c) => {
          if (c.id === childId) {
            return { ...c, parentId };
          }
          return c;
        }),
        isDirty: true,
      }));
      if (previous) {
        get().recordSkinComponentUpdate(childId, { parentId }, { beforeOverride: previous });
      }
    },

    // Additional actions for SceneHierarchy
    toggleHotspotVisibility: async (_hotspotId) => {
      // This will be implemented by the Editor component
      // For now, it's a placeholder that will be overridden
      console.warn(
        "toggleHotspotVisibility not implemented in store - should be handled by Editor",
      );
    },

    uploadPanoramas: async (_files, _uploadPreference, _mediaType) => {
      // This will be implemented by the Editor component
      // For now, it's a placeholder that will be overridden
      console.warn("uploadPanoramas not implemented in store - should be handled by Editor");
    },

    deleteHotspotWithUndo: async (_hotspotId) => {
      // This will be implemented by the Editor component
      // For now, it's a placeholder that will be overridden
      console.warn("deleteHotspotWithUndo not implemented in store - should be handled by Editor");
    },

    // Additional actions for ThreeJSPanoramaViewer
    updateHotspotFromViewer: async (hotspotId, updates, options) => {
      const stateBefore = get();
      const hotspotBefore = stateBefore.hotspots.find((h) => h.id === hotspotId);
      const snapshotCurrent = hotspotBefore ? buildHotspotSnapshot(hotspotBefore) : null;
      const snapshotBefore = snapshotCurrent
        ? ({ ...snapshotCurrent, ...(options?.historyBeforeOverride || {}) } as HotspotSnapshot)
        : null;
      const snapshotAfter = snapshotCurrent
        ? ({ ...snapshotCurrent, ...updates } as HotspotSnapshot)
        : null;
      const useLocalBackend = shouldUseLocalBackend(stateBefore);

      // Apply immediately so drag/drop doesn't visually snap back while persistence is in-flight.
      set((state) => ({
        hotspots: state.hotspots.map((h) =>
          h.id === hotspotId ? ({ ...h, ...updates } as Hotspot) : h,
        ),
        selectedHotspot:
          state.selectedHotspot?.id === hotspotId
            ? ({ ...state.selectedHotspot, ...updates } as Hotspot)
            : state.selectedHotspot,
      }));

      try {
        if (useLocalBackend) {
          const craftspaceId = get().project?.craftspace_id || "default";
          await updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, ...updates });
        } else {
          await convex.mutation(api.hotspots.update, buildHotspotUpdateArgs(hotspotId, updates));
        }

        if (snapshotBefore && snapshotAfter) {
          get().recordHotspotUpdateWithSnapshots(hotspotId, snapshotBefore, snapshotAfter);
        }
      } catch (error) {
        if (hotspotBefore) {
          set((state) => ({
            hotspots: state.hotspots.map((h) => (h.id === hotspotId ? hotspotBefore : h)),
            selectedHotspot:
              state.selectedHotspot?.id === hotspotId ? hotspotBefore : state.selectedHotspot,
          }));
        }
        console.error("Error updating hotspot from viewer:", error);
        throw error;
      }
    },

    requestHotspotPlacement: async (position, user) => {
      // requestHotspotPlacement called
      // Using static import (already imported at top)

      const state = get();

      const userRef = user as UserRef | null;
      const useLocalBackend = shouldUseLocalBackend(state);
      if (!state.selectedPanorama || !state.project || (!userRef?.id && !useLocalBackend)) {
        // Early return - missing data
        return;
      }

      try {
        if (state.pendingHotspotPlacementId) {
          const hotspotId = state.pendingHotspotPlacementId;
          const existingHotspot = state.hotspots.find((h) => h.id === hotspotId);
          let nextContent: Record<string, unknown> = {};
          if (existingHotspot?.content) {
            if (typeof existingHotspot.content === "string") {
              try {
                nextContent = JSON.parse(existingHotspot.content) as Record<string, unknown>;
              } catch {
                nextContent = {};
              }
            } else if (typeof existingHotspot.content === "object") {
              nextContent = { ...(existingHotspot.content as Record<string, unknown>) };
            }
          }
          nextContent.positioned = true;
          set({ pendingHotspotPlacementId: null, placementMode: false });
          await get().updateHotspotFromViewer(hotspotId, {
            position,
            content: nextContent,
            visible: true,
          });
          return;
        }

        // Check placement type to determine what kind of hotspot to create
        const craftspaceId = state.project.craftspace_id || "default";
        if (state.placementType === "html") {
          // Create HTML hotspot and save to database
          const existingHotspots = state.hotspots.filter(
            (h) => h.panorama_id === state.selectedPanorama?.id,
          );
          const nextNumber = existingHotspots.length + 1;
          const hotspotName = `Hotspot ${nextNumber}`;

          const newHTMLHotspot = {
            project_id: state.project.id,
            craftspace_id: craftspaceId,
            panorama_id: state.selectedPanorama.id,
            name: hotspotName,
            type: "html",
            position: position,
            scale: { x: 1, y: 1, z: 1 },
            content: {
              text: "",
              contentType: state.htmlPlacementContentType,
              textColor: "#000000",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              borderColor: "#ffffff",
              borderWidth: 0,
              borderRadius: 0,
              padding: 0,
              fontSize: "3px",
              fontWeight: "500",
              textAlign: "center",
              lineHeight: "1",
              textShadow: "none",
              variant: "default",
              showPointer: false,
              pointerDirection: "bottom",
              pointerSize: 8,
              pointerOverlap: 2,
              tooltipArrowDirection: "top",
              renderStyle: state.hotspotRenderStyle,
            },
            visible: true,
            order: existingHotspots.length,
          };

          // Save to database
          let hotspotData: Hotspot;
          if (useLocalBackend) {
            const id = await createLocalHotspot(newHTMLHotspot);
            const pos = newHTMLHotspot.position as { x: number; y: number; z: number };
            hotspotData = {
              ...newHTMLHotspot,
              id,
              _id: id,
              position_x: pos.x,
              position_y: pos.y,
              position_z: pos.z,
            } as Hotspot;
          } else {
            const { craftspace_id, ...convexPayload } = newHTMLHotspot;
            hotspotData = (await convex.mutation(api.hotspots.create, convexPayload)) as Hotspot;
          }

          // Update store with database response
          set((state) => ({
            hotspots: [...state.hotspots, hotspotData],
            selectedHotspot: hotspotData,
            placementMode: false,
            hotspotPanelTab: state.htmlPlacementContentType === "text" ? "settings" : state.hotspotPanelTab,
            hotspotTextAutoFocus: state.htmlPlacementContentType === "text",
          }));

          get().recordHotspotCreate(hotspotData);

          return;
        }

        if (state.placementType === "video") {
          const existingHotspots = state.hotspots.filter(
            (h) => h.panorama_id === state.selectedPanorama?.id,
          );
          const nextNumber = existingHotspots.length + 1;
          const hotspotName = `Video ${nextNumber}`;

          const newVideoHotspot = {
            project_id: state.project.id,
            craftspace_id: craftspaceId,
            panorama_id: state.selectedPanorama.id,
            name: hotspotName,
            type: "video",
            position: position,
            scale: { x: 1, y: 1, z: 1 },
            content: {
              videoUrl: "",
              videoLoop: true,
              videoMuted: true,
              videoAutoplay: true,
              videoWidth: 80,
              videoHeight: 45,
              chromaKeyEnabled: true,
              chromaKeyColor: "#00ff00",
              chromaKeySimilarity: 0.12,
              chromaKeySmoothness: 0.08,
              chromaKeySpill: 0.2,
              renderStyle: state.hotspotRenderStyle,
            },
            visible: true,
            order: existingHotspots.length,
          };

          let hotspotData: Hotspot;
          if (useLocalBackend) {
            const id = await createLocalHotspot(newVideoHotspot);
            const pos = newVideoHotspot.position as { x: number; y: number; z: number };
            hotspotData = {
              ...newVideoHotspot,
              id,
              _id: id,
              position_x: pos.x,
              position_y: pos.y,
              position_z: pos.z,
            } as Hotspot;
          } else {
            const { craftspace_id, ...convexPayload } = newVideoHotspot;
            hotspotData = (await convex.mutation(api.hotspots.create, convexPayload)) as Hotspot;
          }

          set((state) => ({
            hotspots: [...state.hotspots, hotspotData],
            selectedHotspot: hotspotData,
            placementMode: false,
          }));

          get().recordHotspotCreate(hotspotData);

          return;
        }

        // Default: Create SVG hotspot (existing logic)
        const existingHotspots = state.hotspots.filter(
          (h) => h.panorama_id === state.selectedPanorama?.id,
        );
        const nextNumber = existingHotspots.length + 1;
        const hotspotName = `Hotspot ${nextNumber}`;

        // Create the proper SVG for the hotspot
        const hotspotSVG = `<svg width='256' height='256' viewBox='0 0 256 256'>
        <circle cx='128' cy='128' r='110' fill='white' opacity='0.5'/>
        <circle cx='128' cy='128' r='90' fill='white' opacity='1'/>
        <circle cx='128' cy='128' r='70' fill='#2196f3'/>
      </svg>`;

        // Create new SVG hotspot
        const newHotspot = {
          project_id: state.project.id,
          craftspace_id: craftspaceId,
          panorama_id: state.selectedPanorama.id,
          name: hotspotName,
          type: "svg",
          position: position,
          scale: { x: 1, y: 1, z: 1 },
          content: {
            icon: "ℹ︎",
            svg: hotspotSVG,
            title: hotspotName,
            description: "Click to edit this hotspot",
            color: "#2196f3", // Set the background color to match the SVG
            renderStyle: state.hotspotRenderStyle,
          },
          visible: true,
          order: existingHotspots.length,
        };

        let hotspotData: Hotspot;
        if (useLocalBackend) {
          const id = await createLocalHotspot(newHotspot);
          const pos = newHotspot.position as { x: number; y: number; z: number };
          hotspotData = {
            ...newHotspot,
            id,
            _id: id,
            position_x: pos.x,
            position_y: pos.y,
            position_z: pos.z,
          } as Hotspot;
        } else {
          hotspotData = (await convex.mutation(api.hotspots.create, newHotspot)) as Hotspot;
        }

        // Update store
        set((state) => ({
          hotspots: [...state.hotspots, hotspotData],
          selectedHotspot: hotspotData,
          placementMode: false,
        }));

        get().recordHotspotCreate(hotspotData);
      } catch (error) {
        console.error("Error creating hotspot:", error);
        throw error;
      }
    },

    // Create HTML hotspot (in memory only - proof of concept)
    createHTMLHotspot: (position: { x: number; y: number; z: number }, user: UserRef) => {
      const state = get();
      if (!state.selectedPanorama || !state.project) {
        throw new Error("No panorama or project selected");
      }

      try {
        const hotspotName = "Hotspot";

        // Create new HTML hotspot in memory only
        const newHotspot = {
          id: `html-hotspot-${Date.now()}`, // Generate temporary ID
          project_id: state.project.id,
          panorama_id: state.selectedPanorama.id,
          user_id: user.id,
          name: hotspotName,
          type: "html",
          position: position,
          scale: { x: 1, y: 1, z: 1 },
          content: {
            text: "",
            textColor: "#000000",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            borderColor: "#ffffff",
            borderWidth: 0,
            borderRadius: 0,
            padding: 0,
            fontSize: "3px",
            fontWeight: "500",
            textAlign: "center",
            lineHeight: "1",
            textShadow: "none",
            variant: "default",
            showPointer: false,
            pointerDirection: "bottom",
            pointerSize: 8,
            pointerOverlap: 2,
            tooltipArrowDirection: "top",
            renderStyle: state.hotspotRenderStyle,
          },
          visible: true,
          order: state.hotspots.filter((h) => h.panorama_id === state.selectedPanorama?.id).length,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        // Update store directly (no database save)
        set((state) => ({
          hotspots: [...state.hotspots, newHotspot],
          selectedHotspot: newHotspot,
          placementMode: false,
        }));

        return newHotspot;
      } catch (error) {
        console.error("Error creating HTML hotspot:", error);
        throw error;
      }
    },

    // Additional actions for PropertiesPanel
    updateHotspotFromPanel: async (_hotspotId, _updates) => {
      // This will be implemented by the Editor component
      // For now, it's a placeholder that will be overridden
      console.warn("updateHotspotFromPanel not implemented in store - should be handled by Editor");
    },

    deleteHotspotFromPanel: async (_hotspotId) => {
      // This will be implemented by the Editor component
      // For now, it's a placeholder that will be overridden
      console.warn("deleteHotspotFromPanel not implemented in store - should be handled by Editor");
    },

    // Update panorama in store
    updatePanorama: (panoramaId, updates) =>
      set((state) => ({
        panoramas: state.panoramas.map((p) => (p.id === panoramaId ? { ...p, ...updates } : p)),
        selectedPanorama:
          state.selectedPanorama?.id === panoramaId
            ? { ...state.selectedPanorama, ...updates }
            : state.selectedPanorama,
      })),

    // Additional actions for PanoramaPropertiesPanel
    updatePanoramaFromPanel: async (_panoramaId, _updates) => {
      // This will be implemented by the Editor component
      // For now, it's a placeholder that will be overridden
      console.warn(
        "updatePanoramaFromPanel not implemented in store - should be handled by Editor",
      );
    },

    deletePanoramaFromPanel: async (_panoramaId) => {
      // This will be implemented by the Editor component
      // For now, it's a placeholder that will be overridden
      console.warn(
        "deletePanoramaFromPanel not implemented in store - should be handled by Editor",
      );
    },

    // Hotspot Visibility Actions
    showHotspots: (hotspotIds) =>
      set((state) => ({
        hotspots: state.hotspots.map((h) =>
          hotspotIds.includes(h.id) ? { ...h, visible: true } : h,
        ),
        isDirty: true,
      })),
    hideHotspots: (hotspotIds) =>
      set((state) => ({
        hotspots: state.hotspots.map((h) =>
          hotspotIds.includes(h.id) ? { ...h, visible: false } : h,
        ),
        isDirty: true,
      })),
    toggleHotspots: (hotspotIds) =>
      set((state) => ({
        hotspots: state.hotspots.map((h) =>
          hotspotIds.includes(h.id) ? { ...h, visible: !h.visible } : h,
        ),
        isDirty: true,
      })),
    setHotspotVisibility: (hotspotId, visible) =>
      set((state) => ({
        hotspots: state.hotspots.map((h) => (h.id === hotspotId ? { ...h, visible } : h)),
        isDirty: true,
      })),

    // Skin Visibility Actions (runtime-only; do not mark project dirty)
    showSkinComponents: (componentIds) =>
      set((state) => ({
        skinComponents: state.skinComponents.map((c) =>
          componentIds.includes(c.id) ? { ...c, visible: true } : c,
        ),
      })),
    hideSkinComponents: (componentIds) =>
      set((state) => ({
        skinComponents: state.skinComponents.map((c) =>
          componentIds.includes(c.id) ? { ...c, visible: false } : c,
        ),
      })),
    toggleSkinComponents: (componentIds) =>
      set((state) => ({
        skinComponents: state.skinComponents.map((c) =>
          componentIds.includes(c.id) ? { ...c, visible: !c.visible } : c,
        ),
      })),

    // Bulk editing actions
    bulkUpdateHotspots: async (hotspotIds, updates) => {
      console.log("🔍 Store bulkUpdateHotspots called with:", { hotspotIds, updates });
      try {
        // Update local store immediately for responsive UI
        set((state) => {
          const updatedHotspots = state.hotspots.map((h) => {
            if (hotspotIds.includes(h.id)) {
              // If there are content updates, merge them with existing content
              if (updates.content) {
                const currentContent = asRecord(h.content);
                const mergedContent = { ...currentContent, ...updates.content };
                console.log("🔍 Merging content for hotspot", h.id, ":", {
                  currentContent,
                  updates: updates.content,
                  mergedContent,
                });
                return { ...h, ...updates, content: mergedContent };
              } else {
                return { ...h, ...updates };
              }
            }
            return h;
          });
          console.log(
            "🔍 Updated hotspots in store:",
            updatedHotspots.filter((h) => hotspotIds.includes(h.id)),
          );
          return {
            hotspots: updatedHotspots,
            isDirty: true,
          };
        });

        // Update database - need to handle content updates specially
        if (updates.content) {
          console.log("🔍 Updating database with content updates for each hotspot individually");
          // Update each hotspot individually to merge content properly
          for (const hotspotId of hotspotIds) {
            const hotspot = get().hotspots.find((h) => h.id === hotspotId);
            if (hotspot) {
              const currentContent = asRecord(hotspot.content);
              const mergedContent = { ...currentContent, ...updates.content };
              if (shouldUseLocalBackend(get())) {
                const craftspaceId = get().project?.craftspace_id || "default";
                await updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, content: mergedContent });
              } else {
                await convex.mutation(api.hotspots.update, {
                  id: hotspotId,
                  content: mergedContent,
                });
              }
            }
          }
        } else {
          console.log("🔍 Updating database with direct updates:", updates);
          if (shouldUseLocalBackend(get())) {
            const craftspaceId = get().project?.craftspace_id || "default";
            await Promise.all(
              hotspotIds.map((hotspotId) => updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, ...updates })),
            );
          } else {
            await Promise.all(
              hotspotIds.map((hotspotId) =>
                convex.mutation(api.hotspots.update, buildHotspotUpdateArgs(hotspotId, updates)),
              ),
            );
          }
        }
        console.log("✅ Database update successful");
      } catch (error) {
        console.error("❌ Error bulk updating hotspots:", error);
        throw error;
      }
    },

    bulkDeleteHotspots: async (hotspotIds) => {
      try {
        // Update local store immediately for responsive UI
        set((state) => ({
          hotspots: state.hotspots.filter((h) => !hotspotIds.includes(h.id)),
          selectedHotspotIds: state.selectedHotspotIds.filter((id) => !hotspotIds.includes(id)),
          isDirty: true,
        }));

        // Delete from database
        if (shouldUseLocalBackend(get())) {
          const craftspaceId = get().project?.craftspace_id || "default";
          await Promise.all(hotspotIds.map((hotspotId) => removeLocalHotspot(hotspotId, craftspaceId)));
        } else {
          await Promise.all(
            hotspotIds.map((hotspotId) => convex.mutation(api.hotspots.remove, { id: hotspotId })),
          );
        }
      } catch (error) {
        console.error("Error bulk deleting hotspots:", error);
        throw error;
      }
    },

    // Camera view actions
    setCurrentCameraView: (view) => set({ currentCameraView: view }),
    setDefaultViewSaveEvent: (event) => set({ defaultViewSaveEvent: event }),
    setPendingPanoramaView: (payload) => set({ pendingPanoramaView: payload }),
    requestFocusHotspot: (hotspotId) => set({ focusHotspotRequestId: hotspotId }),
    setProjectBackend: (backend) => set({ projectBackend: backend }),
  };
});
